import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';

const GamesIntellectualProperty = () => {
  const [gameStates, setGameStates] = useState({});
  const [loadingMethod, setLoadingMethod] = useState('iframe');
  const [gameCompletionStates, setGameCompletionStates] = useState({});
  const [resultsVisible, setResultsVisible] = useState({});
  const gameRefs = useRef({});

  const initializeAllGames = useCallback(async () => {
    const games = [
      { id: 'game-0', hash: 'c399eeb36170f59a' },
      { id: 'game-1', hash: '745ff585310beb26' }
    ];
    for (let i = 0; i < games.length; i++) {
      await initializeGameWithIframe(games[i].id, games[i].hash, i);
    }
  }, []);

  useEffect(() => {
    initializeAllGames();
  }, [initializeAllGames]);

  const handleResultsVisibility = useCallback((gameId, visible) => {
    setResultsVisible(prev => ({ ...prev, [gameId]: visible }));
    setGameCompletionStates(prev => ({ ...prev, [gameId]: visible }));
    const container = document.getElementById(gameId);
    if (container) {
      if (visible) {
        container.setAttribute('data-game-state', 'completed');
        container.classList.add('game-results-active');
      }
    }
  }, []);

  const initializeGameWithIframe = async (gameId, hash, index) => {
    const container = document.getElementById(gameId);
    if (!container) return;

    setGameStates(prev => ({ ...prev, [gameId]: 'loading' }));
    container.innerHTML = '';
    
    const gameContainer = document.createElement('div');
    gameContainer.className = 'game-responsive-container';
    
    const updateContainerStyle = () => {
      const isMobile = window.innerWidth < 768;
      const isCompleted = gameCompletionStates[gameId] || resultsVisible[gameId];
      if (isMobile) {
        const height = isCompleted ? '85vh' : '80vh';
        gameContainer.style.cssText = `
          width: calc(100vw - 16px);
          height: ${height};
          max-height: ${isCompleted ? '880px' : '820px'};
          margin: 8px auto;
          padding: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8fafc;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 16px rgba(0,0,0,0.08);
        `;
      } else {
        gameContainer.style.cssText = `
          width: 90%;
          height: ${isCompleted ? '80vh' : '75vh'};
          max-height: 820px;
          margin: 16px auto;
          padding: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8fafc;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0,0,0,0.12);
        `;
      }
    };
    
    updateContainerStyle();
    
    const iframe = document.createElement('iframe');
    iframe.src = `https://p.interacty.me/${hash}/iframe.html`;
    iframe.style.cssText = `width: 100%; height: 100%; border: none; scrolling: no;`;
    iframe.setAttribute('scrolling', 'no');
    
    iframe.onload = () => {
      setGameStates(prev => ({ ...prev, [gameId]: 'loaded' }));
      const applyScaling = () => {
        const isMobile = window.innerWidth < 768;
        const isCompleted = gameCompletionStates[gameId] || resultsVisible[gameId];
        if (isMobile) {
          const scale = isCompleted ? 0.88 : 0.90;
          iframe.style.transform = `scale(${scale})`;
          iframe.style.width = `${(1 / scale) * 100}%`;
          iframe.style.height = `${(1 / scale) * 100}%`;
          iframe.style.margin = '0 auto';
        } else {
          const scale = isCompleted ? 0.97 : 0.99;
          iframe.style.transform = `scale(${scale})`;
          iframe.style.width = `${(1 / scale) * 100}%`;
          iframe.style.height = `${(1 / scale) * 100}%`;
          iframe.style.margin = '0 auto';
        }
      };
      applyScaling();
      window.addEventListener('resize', applyScaling);
      
      iframe.contentWindow?.addEventListener('message', (event) => {
        if (event.data && (event.data.gameComplete || event.data.showResults)) {
          handleResultsVisibility(gameId, true);
          updateContainerStyle();
          applyScaling();
        }
      });
    };
    
    gameContainer.appendChild(iframe);
    container.appendChild(gameContainer);
  };

  const games = [
    { id: 'game-0', title: "🧑🏻‍⚖️ Propiedad industrial 👨🏽‍⚖️", hash: "c399eeb36170f59a" },
    { id: 'game-1', title: "🧑🏻‍⚖️ Derecho de autor y derechos conexos 👨🏽‍⚖️", hash: "745ff585310beb26" }
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-7xl mx-auto px-2 sm:px-4 py-6">
      <div className="text-center mb-6">
        <h1 className="text-3xl md:text-5xl font-bold bg-gradient-to-r from-purple-500 to-blue-500 bg-clip-text text-transparent mb-3">
          Juegos - Propiedad intelectual
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300 mb-6 px-2">
          Aprende jugando sobre las categorías de la propiedad intelectual
        </p>

        {/* AJUSTE SOLICITADO: Recuadro de instrucciones */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mb-10 max-w-2xl mx-auto border border-indigo-100 dark:border-gray-700"
        >
          <div className="space-y-2 text-gray-700 dark:text-gray-200 font-medium leading-relaxed">
            <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400 mb-4">¡Vamos a jugar! 🎯</p>
            <p>Busca dos (2) cartas iguales. 🎴🎴</p>
            <p>Primero, darás clic en iniciar. ▶️</p>
            <p>Luego, memorizarás las posiciones durante siete (7) segundos. 🧠 ⏰</p>
            <p>Encuentra todas las parejas para ganar. 🏆</p>
            <p>Aprende sobre las categoría de la propiedad intelectual. 📖</p>
            <p className="text-lg font-bold text-purple-600 dark:text-purple-400 mt-4">¡Muchos éxitos! 🎉!</p>
          </div>
        </motion.div>
      </div>

      <div className="space-y-6">
        {games.map((game, index) => (
          <motion.div 
            key={game.id} 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: index * 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4"
          >
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">
              {game.title}
            </h2>
            <div id={game.id} className="w-full game-container">
              {gameStates[game.id] === 'loading' && (
                <div className="flex items-center justify-center h-96">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default GamesIntellectualProperty;